import Phaser from 'phaser';
import type { NodeType } from '../sim/types';
import { nodeStyle } from './palette';

/**
 * Draws the inner glyph for a given node type onto a Phaser Graphics object,
 * centered at (0, 0) in local coords. Pulled out of NodeView so other UI
 * (tutorial popups, legend rows) can render the same marks.
 *
 * `radius` is the visual node radius the glyph is sized against (default 34
 * to match NodeView). All internal lengths scale relative to this.
 */
export function drawNodeMark(
  g: Phaser.GameObjects.Graphics,
  type: NodeType,
  radius = 34,
) {
  const style = nodeStyle(type);
  const c = style.ring;
  const cActive = style.ringActive;

  // base scale relative to default radius 34
  const s = radius / 34;
  const sx = (n: number) => n * s;

  g.lineStyle(2, cActive, 1);
  g.fillStyle(cActive, 0.95);

  switch (type) {
    case 'required':
      g.fillTriangle(0, sx(-10), sx(9), 0, 0, sx(10));
      g.fillTriangle(0, sx(-10), sx(-9), 0, 0, sx(10));
      g.lineStyle(1.5, 0xffffff, 0.4);
      g.strokeCircle(0, 0, sx(13));
      break;
    case 'protected':
      g.lineStyle(2.5 * s, cActive, 1);
      g.lineBetween(sx(-8), sx(-8), sx(8), sx(8));
      g.lineBetween(sx(-8), sx(8), sx(8), sx(-8));
      g.lineStyle(1.5 * s, c, 0.6);
      g.lineBetween(sx(-12), sx(-8), sx(-12), sx(-12));
      g.lineBetween(sx(-12), sx(-12), sx(-8), sx(-12));
      g.lineBetween(sx(12), sx(-8), sx(12), sx(-12));
      g.lineBetween(sx(12), sx(-12), sx(8), sx(-12));
      g.lineBetween(sx(-12), sx(8), sx(-12), sx(12));
      g.lineBetween(sx(-12), sx(12), sx(-8), sx(12));
      g.lineBetween(sx(12), sx(8), sx(12), sx(12));
      g.lineBetween(sx(12), sx(12), sx(8), sx(12));
      break;
    case 'relay':
      g.lineStyle(2.5 * s, cActive, 1);
      g.beginPath();
      g.moveTo(sx(-8), sx(-8)); g.lineTo(sx(2), 0); g.lineTo(sx(-8), sx(8));
      g.strokePath();
      g.beginPath();
      g.moveTo(0, sx(-8)); g.lineTo(sx(10), 0); g.lineTo(0, sx(8));
      g.strokePath();
      break;
    case 'shield':
      g.lineStyle(2 * s, cActive, 1);
      g.beginPath();
      g.moveTo(0, sx(-11));
      g.lineTo(sx(9), sx(-7));
      g.lineTo(sx(9), sx(3));
      g.lineTo(0, sx(11));
      g.lineTo(sx(-9), sx(3));
      g.lineTo(sx(-9), sx(-7));
      g.closePath();
      g.strokePath();
      g.lineBetween(sx(-5), sx(-2), sx(5), sx(-2));
      break;
    case 'splitter':
      g.lineStyle(2.5 * s, cActive, 1);
      g.lineBetween(0, 0, 0, sx(-10));
      g.lineBetween(0, 0, sx(-9), sx(7));
      g.lineBetween(0, 0, sx(9), sx(7));
      g.fillCircle(0, sx(-10), sx(2));
      g.fillCircle(sx(-9), sx(7), sx(2));
      g.fillCircle(sx(9), sx(7), sx(2));
      g.fillCircle(0, 0, sx(2.5));
      break;
    case 'capacitor':
      g.lineStyle(2.5 * s, cActive, 1);
      g.lineBetween(sx(-12), 0, sx(-4), 0);
      g.lineBetween(sx(4), 0, sx(12), 0);
      g.lineBetween(sx(-4), sx(-8), sx(-4), sx(8));
      g.lineBetween(sx(4), sx(-8), sx(4), sx(8));
      break;
    case 'core':
      g.lineStyle(2 * s, cActive, 1);
      g.strokeCircle(0, 0, sx(11));
      g.lineStyle(1.5 * s, cActive, 0.7);
      g.strokeCircle(0, 0, sx(7));
      g.fillCircle(0, 0, sx(3));
      g.lineStyle(1, cActive, 0.4);
      g.lineBetween(sx(-14), 0, sx(-12), 0);
      g.lineBetween(sx(12), 0, sx(14), 0);
      g.lineBetween(0, sx(-14), 0, sx(-12));
      g.lineBetween(0, sx(12), 0, sx(14));
      break;
    case 'normal':
    default: {
      g.lineStyle(1.5 * s, c, 0.85);
      const r = sx(9);
      g.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r;
        if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
      }
      g.closePath();
      g.strokePath();
      g.fillStyle(cActive, 1);
      g.fillCircle(0, 0, sx(2.5));
      break;
    }
  }
}

export function nodeTypeTitle(t: NodeType): string {
  switch (t) {
    case 'normal':    return 'NODE';
    case 'required':  return 'REQUIRED';
    case 'protected': return 'PROTECTED';
    case 'relay':     return 'RELAY';
    case 'shield':    return 'SHIELD';
    case 'splitter':  return 'SPLITTER';
    case 'capacitor': return 'CAPACITOR';
    case 'core':      return 'CORE';
  }
}

export function nodeTypeAccentHex(t: NodeType): string {
  switch (t) {
    case 'required':  return '#6cf0ff';
    case 'protected': return '#ff7a99';
    case 'relay':     return '#ffd070';
    case 'shield':    return '#88ffb0';
    case 'splitter':  return '#ff99dd';
    case 'capacitor': return '#fff088';
    case 'core':      return '#dd99ff';
    case 'normal':
    default:          return '#8aa6ff';
  }
}
